/**
 * Theme store — runtime theme switching.
 *
 * Mammas Canteen (default) is the teal/white Liquid Glass look.
 * St. Pauli FC Fans is a warm brown + off-white + red palette.
 * Dark mode is orthogonal — each theme has its own dark override in CSS.
 *
 * The theme is applied via `data-theme` on <html>; CSS variables cascade
 * and every Tailwind class that reads them (`bg-primary`, `text-on-surface`,
 * `bg-primary/10`, …) picks up the new values without a rebuild.
 */

import { create } from 'zustand'

export const THEMES = [
  {
    id: 'mammas',
    label: 'Mammas Canteen',
    tagline: 'Teal Liquid Glass · Standard',
    preview: { primary: '#515f74', surface: '#f7f9fb', accent: '#8aa4c8' },
  },
  {
    id: 'stpauli',
    label: 'St. Pauli FC Fans',
    tagline: 'Warme Tobacco-Tönung · Kiez-Industrie',
    preview: { primary: '#4F372D', surface: '#FDFCFB', accent: '#E30613' },
  },
] as const

export type ThemeId = (typeof THEMES)[number]['id']

const STORAGE_KEY = 'mc-erp.themeId'
const DEFAULT_THEME: ThemeId = 'mammas'

function readInitial(): ThemeId {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (raw && THEMES.some(t => t.id === raw)) return raw as ThemeId
  } catch { /* no-op */ }
  return DEFAULT_THEME
}

function persist(id: ThemeId) {
  try { window.localStorage.setItem(STORAGE_KEY, id) } catch { /* ignore */ }
}

/**
 * Apply the theme to <html>. Mammas is the implicit default (no attr); we
 * still set it explicitly so CSS selectors like `[data-theme="mammas"]`
 * work identically to `[data-theme="stpauli"]`.
 */
export function applyThemeToDocument(id: ThemeId) {
  try {
    document.documentElement.setAttribute('data-theme', id)
  } catch { /* no-DOM env */ }
}

interface ThemeState {
  themeId: ThemeId
  setTheme: (id: ThemeId) => void
}

export const useThemeStore = create<ThemeState>((set) => ({
  themeId: typeof window !== 'undefined' ? readInitial() : DEFAULT_THEME,
  setTheme: (id) => { persist(id); applyThemeToDocument(id); set({ themeId: id }) },
}))

/**
 * Pre-React bootstrap — call this in main.tsx BEFORE ReactDOM.render.
 * Avoids a flash of the default theme for users who picked St. Pauli.
 */
export function bootstrapTheme() {
  if (typeof window === 'undefined') return
  applyThemeToDocument(readInitial())
}
